import useWorkflowNavigation from '@/frontend/pages/Dashboard/Workflows/useWorkflowNavigation'
import { Link, useForm } from '@inertiajs/react'
import type { FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'
import type { Copy } from '@/frontend/pages/Dashboard/Workflows/types'

export default function ConnectionForm({ tenant, copy, configurationPath }: { tenant: string | null; copy: Copy; configurationPath?: string | null }) {
  const { connectionPath } = useWorkflowNavigation()
  const form = useForm({ praxis: { tenant: tenant ?? '', api_key: '' } })
  const tenantError = form.errors['praxis.tenant']
  const keyError = form.errors['praxis.api_key']

  function submit(event: FormEvent) {
    event.preventDefault()
    form.patch(connectionPath, {
      preserveScroll: true,
      onFinish: () => form.setData('praxis', { ...form.data.praxis, api_key: '' }),
    })
  }

  if (configurationPath) return <section className="space-y-3 rounded-xl border bg-card p-5">
    <h2 className="text-base font-semibold">{copy.project_connection.title}</h2>
    <p className="text-sm text-muted-foreground">{copy.project_connection.description}</p>
    <Button asChild variant="outline" size="sm"><Link href={configurationPath}>{copy.project_connection.configure}</Link></Button>
  </section>

  return <section className="space-y-4 rounded-xl border bg-card p-5">
    <div className="space-y-1">
      <h2 className="text-base font-semibold">{copy.connection_form.title}</h2>
      <p className="text-sm text-muted-foreground">{copy.connection_form.description}</p>
    </div>
    <form onSubmit={submit} className="space-y-4" aria-busy={form.processing}>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="praxis-tenant">{copy.connection_form.tenant}</Label>
          <Input id="praxis-tenant" name="praxis[tenant]" value={form.data.praxis.tenant} required
            disabled={form.processing} autoComplete="off" aria-invalid={!!tenantError}
            aria-describedby={tenantError ? 'praxis-tenant-error' : undefined}
            onChange={(event) => form.setData('praxis', { ...form.data.praxis, tenant: event.target.value })} />
          {tenantError && <p id="praxis-tenant-error" role="alert" className="text-sm text-destructive">{tenantError}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="praxis-api-key">{copy.connection_form.api_key}</Label>
          <Input id="praxis-api-key" name="praxis[api_key]" type="password" value={form.data.praxis.api_key} required
            disabled={form.processing} autoComplete="new-password" aria-invalid={!!keyError}
            aria-describedby={keyError ? 'praxis-key-error' : 'praxis-key-hint'}
            onChange={(event) => form.setData('praxis', { ...form.data.praxis, api_key: event.target.value })} />
          {keyError && <p id="praxis-key-error" role="alert" className="text-sm text-destructive">{keyError}</p>}
        </div>
      </div>
      <p id="praxis-key-hint" className="text-xs text-muted-foreground">{copy.connection_form.key_hint}</p>
      <Button type="submit" disabled={form.processing}>
        {form.processing && <Loader2 className="size-4 animate-spin" aria-hidden />}
        {form.processing ? copy.connection_form.submitting : copy.connection_form.submit}
      </Button>
    </form>
  </section>
}
